import { DestroyRef, Injectable, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, finalize, forkJoin, map, takeUntil, timeout } from 'rxjs';

import { CallFacadeService } from './call-facade.service';
import { CallTabSyncService } from './call-tab-sync.service';
import { AgentDesktopService } from '../../services/agent-desktop.service';
import { RejectFlowOwner } from '../models/reject-flow-owner.type';
import { DeclineReasonItemDto } from 'src/core/models/decline-reason/decline-reason-item.dto';
import type {
  RejectFlowStep,
  RejectReasonOption,
} from '../../components/reject-call-dialog/models/reject-flow.model';

/** `RejectSegmentInput.RejectReason` contract: 0 means "no reason provided". */
const NO_DECLINE_REASON_ID = 0;
/** Upper bound on how long the SIP hangup waits for the reason to be persisted. */
const REJECT_SEGMENT_HANGUP_TIMEOUT_MS = 3000;

interface RejectFlowStartOptions {
  /** Queue the ringing call arrived on; null for a direct/internal call. Scopes the reason list. */
  readonly queueName: string | null;
  /**
   * Who is calling (name or number), shown inside the dialog. Off-route the backdrop covers the
   * toolbar's incoming-call toast — the toast is trapped in LeptonX's `.lpx-toolbar-container`
   * stacking context (z-index 1021) while the backdrop sits at the root — so without this the agent
   * would be picking a decline reason with the caller's identity dimmed out behind the modal.
   */
  readonly callerLabel?: string | null;
  /**
   * Host-owned cleanup fired once, the moment the reject is committed (before the HTTP call).
   * Each host clears its OWN incoming-call toast and stops its tones here — those side effects are
   * deliberately not baked into this service, which owns only the flow state and the reject API call.
   */
  readonly onRejectCommitted?: () => void;
}

/**
 * Single owner of the decline-reason reject flow (`idle` → `confirm` → `reason` → reject + hangup).
 *
 * Root-provided so the flow is **route-independent**: it is driven either by `AgentDesktopComponent`
 * (on `/agent-desktop`) or by `AgentStatusToolbarComponent`'s always-present incoming-call toast (on
 * any other page). Keeping one instance instead of duplicating the state machine in both hosts
 * guarantees a single dialog, a single in-flight `rejectSegment` call, and one consistent reset path.
 *
 * `owner` records which host opened the flow so only that host's dialog surface renders it — both
 * components are alive at the same time on `/agent-desktop`, and an ungated `step` signal would
 * stack two identical modals. See diagrams/06-reject-call-dialog-flow.md.
 */
@Injectable({ providedIn: 'root' })
export class RejectCallFlowService {
  private readonly facade = inject(CallFacadeService);
  private readonly callTabSync = inject(CallTabSyncService);
  private readonly agentDesktopService = inject(AgentDesktopService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _step = signal<RejectFlowStep>('idle');
  private readonly _selectedReason = signal<string | null>(null);
  private readonly _reasonOptions = signal<readonly RejectReasonOption[]>([]);
  private readonly _owner = signal<RejectFlowOwner | null>(null);
  private readonly _callerLabel = signal<string | null>(null);

  private queueName: string | null = null;
  private onRejectCommitted: (() => void) | null = null;

  /**
   * Cancels everything in flight for the current flow. A decline-reason request that outlives its
   * flow is dangerous, not merely wasteful: its empty-list and error branches reject the call
   * outright, so a late response after the agent pressed "No" would hang up a call they chose to
   * keep ringing.
   */
  private readonly flowTeardown$ = new Subject<void>();
  /**
   * A reject is already on its way to the backend — internal double-submit guard, deliberately NOT
   * exposed to the dialog. `executeReject` resets the flow in the same synchronous tick it raises
   * this, so `step` is already `idle` and the dialog unmounted before change detection could ever
   * observe it: a `[submitting]` binding driven from here would be permanently dead. The dialog's
   * busy state comes from `loadingReasons` instead, which IS observable on screen.
   */
  private readonly _submitting = signal(false);
  /**
   * A decline-reason load is in flight. Separate from `_submitting`: nothing is committed yet, but
   * the dialog IS on screen (still on the `confirm` step) with a live Yes button. Without this a
   * second tap would fire a second `getDeclineReasons()` — and, on the empty/error branch, a second
   * reject. Exposed so the dialog can disable Yes while it waits; Cancel deliberately stays live.
   */
  private readonly _loadingReasons = signal(false);

  readonly step = this._step.asReadonly();
  readonly selectedReason = this._selectedReason.asReadonly();
  readonly reasonOptions = this._reasonOptions.asReadonly();
  readonly owner = this._owner.asReadonly();
  readonly loadingReasons = this._loadingReasons.asReadonly();
  readonly callerLabel = this._callerLabel.asReadonly();

  private readonly isInboundRinging = this.facade.isInboundRinging;

  constructor() {
    // The caller hung up, another tab answered, or the call was answered here: an open dialog is
    // about a call that no longer exists, so close it wherever it is rendered.
    effect(() => {
      if (!this.isInboundRinging()) {
        untracked(() => this.reset());
      }
    });
  }

  /** True while `host` owns the flow — bind the dialog's visibility to this, never to `step` alone. */
  isOwnedBy(host: RejectFlowOwner): boolean {
    return this._owner() === host;
  }

  /** Step as seen by `host`: `idle` unless `host` opened the flow. */
  stepFor(host: RejectFlowOwner): RejectFlowStep {
    return this._owner() === host ? this._step() : 'idle';
  }

  start(host: RejectFlowOwner, options: RejectFlowStartOptions): void {
    if (!this.isInboundRinging() || this._submitting()) {
      return;
    }

    this.flowTeardown$.next();
    this.queueName = options.queueName;
    this.onRejectCommitted = options.onRejectCommitted ?? null;
    this._owner.set(host);
    this._callerLabel.set(options.callerLabel ?? null);
    this._selectedReason.set(null);
    this._reasonOptions.set([]);
    this._step.set('confirm');
  }

  cancel(): void {
    this.reset();
  }

  proceedToReasonSelection(): void {
    // Re-entrancy guard: the dialog's Yes button stays mounted while the request is in flight, so a
    // double tap would fire two loads — and, on the empty/error branch, two rejects.
    if (
      this._submitting() ||
      this._loadingReasons() ||
      this._step() !== 'confirm' ||
      this._owner() === null
    ) {
      return;
    }

    // A call with no queue - an agent-to-agent transfer, a direct extension dial - is scoped to the
    // agent's own queues instead. Showing every reason in the tenant would offer reasons belonging
    // to queues this agent does not work.
    const queueName = this.queueName;
    const reasons$ = this.agentDesktopService.getDeclineReasons();
    const source$ = queueName
      ? reasons$.pipe(
          map((items: DeclineReasonItemDto[]) =>
            items.filter(i => (i.queues ?? []).some(q => q.name === queueName)),
          ),
        )
      : forkJoin([reasons$, this.agentDesktopService.getAssignedQueues()]).pipe(
          map(([items, assignedQueues]) => {
            const assignedQueueNames = new Set(assignedQueues.map(q => q.name));
            return items.filter(i => (i.queues ?? []).some(q => assignedQueueNames.has(q.name)));
          }),
        );

    this._loadingReasons.set(true);
    source$
      .pipe(
        takeUntil(this.flowTeardown$),
        // Safety net for the teardown path only. MUST come after takeUntil: when a teardown
        // unsubscribes the request, neither `next` nor `error` runs, so without this the flag would
        // stay raised and permanently dead-lock the Yes button of the NEXT flow (back-to-back calls
        // where B starts while A's load is in flight). It is NOT sufficient on its own — finalize
        // fires on completion, and a source that emits without completing would keep the flag up.
        finalize(() => this._loadingReasons.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (filtered: DeclineReasonItemDto[]) => {
          this._loadingReasons.set(false);
          if (!this.canApplyReasonResponse()) {
            return;
          }
          if (filtered.length === 0) {
            this.executeReject(NO_DECLINE_REASON_ID, null);
            return;
          }
          this._reasonOptions.set(filtered.map(i => ({ value: i.id.toString(), label: i.name })));
          this._step.set('reason');
        },
        error: () => {
          this._loadingReasons.set(false);
          if (!this.canApplyReasonResponse()) {
            return;
          }
          this.executeReject(NO_DECLINE_REASON_ID, null);
        },
      });
  }

  selectReason(reason: string): void {
    if (this._step() !== 'reason') {
      return;
    }
    this._selectedReason.set(reason);
  }

  confirm(): void {
    const reasonValue = this._selectedReason();
    if (this._submitting() || !reasonValue || this._owner() === null) {
      return;
    }

    const selectedOption = this._reasonOptions().find(o => o.value === reasonValue);
    this.executeReject(Number(reasonValue), selectedOption?.label ?? reasonValue);
  }

  reset(): void {
    if (this._step() === 'idle' && this._owner() === null) {
      // Already idle: bail before re-setting signals, otherwise `reasonOptions` would emit a fresh
      // `[]` reference on every call and needlessly dirty both OnPush hosts.
      this.queueName = null;
      this.onRejectCommitted = null;
      return;
    }

    this.flowTeardown$.next();
    this._step.set('idle');
    this._selectedReason.set(null);
    this._reasonOptions.set([]);
    this._owner.set(null);
    this._callerLabel.set(null);
    this.queueName = null;
    this.onRejectCommitted = null;
  }

  /**
   * A decline-reason response may only act on the flow that asked for it. `flowTeardown$` already
   * unsubscribes on cancel/reset, but a response that is delivered synchronously in the same tick
   * as the teardown would still slip through — so re-check that a flow is open on a ringing call.
   */
  private canApplyReasonResponse(): boolean {
    return this.isInboundRinging() && this._owner() !== null && this._step() === 'confirm';
  }

  private executeReject(reasonId: number, reasonLabel: string | null): void {
    this._submitting.set(true);

    if (reasonLabel) {
      this.facade.setPendingRejectReason(reasonLabel);
    }

    const hostCleanup = this.onRejectCommitted;
    hostCleanup?.();
    this.reset();
    this.callTabSync.notifyRejected();

    // Ordering is load-bearing: the reason must be persisted BEFORE the SIP reject, otherwise the
    // FreeSwitch hangup consumer closes the segment with RejectReason = 0 first and the agent's
    // choice is discarded. See diagrams/06-reject-call-dialog-flow.md.
    this.agentDesktopService
      .rejectSegment(reasonId)
      .pipe(timeout(REJECT_SEGMENT_HANGUP_TIMEOUT_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.finishReject(),
        error: () => this.finishReject(),
      });
  }

  private finishReject(): void {
    this._submitting.set(false);
    if (!this.isInboundRinging()) {
      return;
    }
    void this.facade.hangup();
  }
}
